import React, { useState, useMemo } from 'react';
import { db } from '../../../../firebase/config';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { formatCurrency, formatPercent } from './helpers';

// Importación de las primitivas de compilación de PDF en el cliente
import { pdf } from '@react-pdf/renderer';
import LiquidacionPDF from './LiquidacionesPdf';

// Utilidades lógicas del Canvas
import { normalizeString } from '../../../../utils/segurosUtils';

/**
 * ReportesTab - Motor de auditoría y liquidaciones profesionales de IB Brokers.
 */
const ReportesTab = ({ maestros }) => {
    const [liquidaciones, setLiquidaciones] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uiError, setUiError] = useState(null); 
    const [filters, setFilters] = useState({ 
        periodo: '', 
        tipoRamo: '',
        modoLiquidacion: '', // 'Canal' o 'Subcanal'
        entidadId: ''        
    });

    // --- LISTAS PARA SELECCIÓN SECUENCIAL ---
    const listaEntidades = useMemo(() => {
        if (!maestros) return [];
        if (filters.modoLiquidacion === 'Canal') {
            return (maestros.canales || []).map(c => ({ id: c.id, ...c, nombre: c.nombreDisplay || c.nombre || c.subcanal, extra: c.tipo || 'Canal' }));
        }
        if (filters.modoLiquidacion === 'Subcanal') {
            return (maestros.subcanales || []).map(s => ({ id: s.id, ...s, nombre: s.nombre, extra: 'Asociado' }));
        }
        return [];
    }, [filters.modoLiquidacion, maestros]);

    // --- ACCIÓN: CONSULTAR Y FILTRAR EN CALIENTE ---
    const handleConsultar = async () => {
        setUiError(null);

        if (!filters.periodo || !filters.modoLiquidacion || !filters.entidadId) {
            setUiError("Por favor, completa los pasos 1, 2 y 3 de forma obligatoria antes de generar el reporte contable.");
            return;
        }

        setLoading(true);
        setLiquidaciones([]);
        try {
            let q = collection(db, 'public/data/liquidaciones');
            // Paso 1: Filtrar por periodo siempre en el Servidor (Eficiencia de costos Firebase)
            q = query(q, where('periodo', '==', filters.periodo));
            
            const snapshot = await getDocs(q);
            const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));

            // Paso 2: Filtrado en Memoria estricto por la entidad y ramo seleccionado
            const filtered = data.filter(item => {
                let matchEntidad = false;
                
                // Unificamos el cruce histórico y moderno de IDs relacionales
                if (filters.modoLiquidacion === 'Canal') {
                    matchEntidad = item.canalNombreId === filters.entidadId || item.subcanalId === filters.entidadId;
                } else {
                    matchEntidad = item.subcanalNombreId === filters.entidadId || item.vendedorAsociadoId === filters.entidadId;
                }

                const matchRamo = filters.tipoRamo 
                    ? (item.tipoRamo === filters.tipoRamo || (filters.tipoRamo === 'ART' && (item.riesgo === 'ART' || item.type === 'contratos')))
                    : true;
                
                return matchEntidad && matchRamo;
            });

            if (filtered.length === 0) {
                setUiError(`No se encontraron registros de liquidaciones de comisiones para el período ${filters.periodo} vinculados a la entidad seleccionada.`);
                setLoading(false);
                return;
            }

            // Paso 3: Cálculo individualizado basado en Snapshots históricos
            const computed = filtered.map(calculateRowReport);
            
            // Ordenamiento por compañía y cliente
            computed.sort((a, b) => {
                const compA = a.compania || '';
                const compB = b.compania || '';
                if (compA < compB) return -1;
                if (compA > compB) return 1;
                return (a.clienteNombre || '').localeCompare(b.clienteNombre || '');
            });

            setLiquidaciones(computed);

        } catch (e) { 
            console.error("Error en consulta masiva de reportes:", e); 
            setUiError("Ocurrió un error al intentar sincronizar los registros de liquidaciones con la base de datos.");
        } finally { 
            setLoading(false); 
        }
    };

    // --- MOTOR DE CÁLCULO DE DISTRIBUCIÓN COMERCIAL (HYBRID CORE) ---
    const calculateRowReport = (liq) => {
        const isPrima = liq.formaLiquidacion === 'Prima';
        const base = isPrima && parseFloat(liq.importePrima) !== 0 ? (liq.importePrima || 0) : (liq.importeComision || 0);
        const baseLabel = isPrima ? 'Por Prima' : 'Por Comisión';
        const esArt = liq.tipoRamo === 'ART' || liq.riesgo === 'ART' || liq.type === 'contratos';

        let pct = 0, desc = 0;

        // ◄◄◄ ESCUDO PROTECTOR DE SNAPSHOTS: Si el documento ya guarda la foto fija comercial, la usamos directo
        const hasSnapshot = liq.comisionOrganizador !== undefined || liq.comisionAsociado !== undefined;

        if (hasSnapshot) {
            if (filters.modoLiquidacion === 'Canal') {
                pct = parseFloat(liq.comisionOrganizador || 0);
                desc = parseFloat(liq.descuentoOrganizador || 0);
            } else {
                pct = parseFloat(liq.comisionAsociado || 0);
                desc = parseFloat(liq.descuentoAsociado || 0);
            }
        } else {
            // --- FALLBACK SEGURO PARA REGISTROS LEGACY (COMPATIBILIDAD) ---
            const subConfig = maestros?.mapaSubcanales?.[liq.vendedorAsociadoId || liq.subcanalNombreId]; 
            const canalConfig = maestros?.mapaCanales?.[liq.subcanalId || liq.canalNombreId] || maestros?.mapaCanales?.[normalizeString(liq.canal)]; 

            if (filters.modoLiquidacion === 'Canal') {
                if (!esArt) {
                    pct = subConfig ? parseFloat(subConfig.patrimoniales?.distribucionCanal || 0) : parseFloat(canalConfig?.porcentajePatrimoniales || 0);
                    desc = subConfig ? parseFloat(subConfig.patrimoniales?.descuentoCanal || 0) : parseFloat(canalConfig?.descuento || 0);
                } else {
                    if (subConfig) {
                        const r = subConfig.artReglas?.find(x => normalizeString(x.compania) === normalizeString(liq.compania));
                        pct = parseFloat(r?.distCanal || 0);
                        desc = parseFloat(r?.descCanal || 0);
                    } else {
                        const r = canalConfig?.artEspeciales?.find(x => normalizeString(x.compania) === normalizeString(liq.compania));
                        pct = r ? parseFloat(r.porcentaje) : parseFloat(canalConfig?.porcentajePatrimoniales || 0);
                        desc = parseFloat(canalConfig?.descuento || 0);
                    }
                }
            } else {
                if (!esArt) {
                    pct = parseFloat(subConfig?.patrimoniales?.distribucionSubcanal || 0);
                    desc = parseFloat(subConfig?.patrimoniales?.descuentoSubcanal || 0);
                } else {
                    const r = subConfig?.artReglas?.find(x => normalizeString(x.compania) === normalizeString(liq.compania));
                    pct = parseFloat(r?.distSub || 0);
                    desc = parseFloat(r?.descSub || 0);
                }
            }
        }

        const bruto = base * (pct / 100);
        const neto = bruto - (bruto * (desc / 100));

        return { 
            ...liq,
            base, baseLabel,
            res_pct: pct,
            // Conservamos bruto y neto forzados a centavos
            res_bruto: Math.round(bruto * 100) / 100,
            res_desc: desc,
            res_neto: Math.round(neto * 100) / 100,
            nroDocumento: liq.nPoliza || liq.nContrato || 'S/D',
            tipoRamoLabel: esArt ? 'ART' : 'PAT'
        };
    };

    const groupDataForReport = () => {
        const grouped = {};
        liquidaciones.forEach(liq => {
            const cia = liq.compania || 'Sin Compañía';
            const forma = liq.baseLabel; 
            if (!grouped[cia]) grouped[cia] = {};
            if (!grouped[cia][forma]) grouped[cia][forma] = [];
            grouped[cia][forma].push(liq);
        });
        return grouped;
    };

    const handleDownloadPDF = async () => {
        setLoading(true);
        setUiError(null);
        try {
            const docElement = (
                <LiquidacionPDF
                    period={filters.periodo}
                    modo={filters.modoLiquidacion === 'Canal' ? 'ORGANIZADOR (CANAL)' : 'ASOCIADO (SUBCANAL)'}
                    entidad={entidadSeleccionadaNombre}
                    totals={totalesComerciales}
                    groupedData={groupDataForReport()}
                />
            );
            
            // Compilamos los primitivos del PDF a un blob vectorial en memoria
            const blob = await pdf(docElement).toBlob();
            const url = URL.createObjectURL(blob);
            
            // Disparamos la descarga directa del archivo sin interferir con el DOM
            const link = document.createElement('a');
            link.href = url;
            link.download = `liquidacion_${entidadSeleccionadaNombre.toLowerCase().replace(/\s+/g, '_')}_${filters.periodo.replace('/', '_')}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            
        } catch (error) {
            console.error("Error al compilar y descargar PDF en memoria:", error);
            setUiError("Ocurrió un error al intentar compilar las liquidaciones en el documento PDF oficial.");
        } finally {
            setLoading(false);
        }
    };

    // --- ESTADOS DERIVADOS GERENCIALES (MÉTRICAS) ---
    const totalesComerciales = useMemo(() => {
        let brutoTotal = 0;
        let netoTotal = 0;
        liquidaciones.forEach(l => {
            brutoTotal += l.res_bruto;
            netoTotal += l.res_neto;
        });
        const retencionesTotal = brutoTotal - netoTotal;
        return { brutoTotal, netoTotal, retencionesTotal };
    }, [liquidaciones]);

    const entidadSeleccionadaNombre = listaEntidades.find(e => e.id === filters.entidadId)?.nombre || 'GENERAL';

    return (
        <div className="space-y-6 text-left font-sans pb-12">
            
            {/* ALERTAS DE UI */}
            {uiError && (
                <div className="p-4 rounded-2xl bg-amber-50 border border-amber-200 text-amber-800 flex items-start gap-3.5 animate-fade-in no-print select-none">
                    <svg className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <div className="text-xs font-bold leading-normal">{uiError}</div>
                </div>
            )}

            {/* PANEL DE FILTROS SECUENCIALES (no-print) */}
            <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100 no-print text-left">
                <div className="flex items-center gap-3 mb-6 select-none">
                    <div className="w-1.5 h-6 bg-slate-900 rounded-full"></div>
                    <h3 className="text-sm font-black text-slate-800 uppercase tracking-widest">Configuración de Liquidación</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-end text-left">
                    {/* PASO 1: PERIODO */}
                    <div className="text-left">
                        <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5">1. Periodo (DD/MM/YYYY)</label>
                        <input 
                            value={filters.periodo} 
                            onChange={e => {
                                setFilters({...filters, periodo: e.target.value, entidadId: ''});
                                setUiError(null);
                            }} 
                            className="w-full border border-gray-200 bg-gray-50 p-3 rounded-xl outline-none font-mono font-bold text-slate-700 focus:ring-2 focus:ring-slate-200 transition-all text-left" 
                            placeholder="01/06/2026" 
                        />
                    </div>

                    {/* PASO 2: MODO */}
                    <div className="text-left">
                        <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5">2. Liquidar a...</label>
                        <select 
                            value={filters.modoLiquidacion} 
                            onChange={e => {
                                setFilters({...filters, modoLiquidacion: e.target.value, entidadId: ''});
                                setUiError(null);
                            }} 
                            className="w-full border border-gray-200 bg-gray-50 p-3 rounded-xl font-bold text-slate-700 outline-none focus:ring-2 focus:ring-slate-200 transition-all text-left"
                            disabled={!filters.periodo}
                        >
                            <option value="">Seleccione Rol...</option>
                            <option value="Canal">CANAL</option>
                            <option value="Subcanal">SUBCANAL ASOCIADO</option>
                        </select>
                    </div>

                    {/* PASO 3: ENTIDAD ESPECÍFICA */}
                    <div className="text-left">
                        <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5">3. Seleccionar Entidad</label>
                        <select 
                            value={filters.entidadId} 
                            onChange={e => {
                                setFilters({...filters, entidadId: e.target.value});
                                setUiError(null);
                            }} 
                            className="w-full border border-gray-200 bg-gray-50 p-3 rounded-xl font-bold text-slate-700 outline-none focus:ring-2 focus:ring-slate-200 transition-all text-left uppercase"
                            disabled={!filters.modoLiquidacion}
                        >
                            <option value="">Seleccione Nombre...</option>
                            {listaEntidades.map(e => (
                                <option key={e.id} value={e.id}>{e.nombre} ({e.extra})</option>
                            ))}
                        </select>
                    </div>

                    {/* PASO 4: RAMO (OPCIONAL) */}
                    <div className="text-left">
                        <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5">4. Ramo (Opcional)</label>
                        <div className="flex gap-2 text-left">
                            <select 
                                value={filters.tipoRamo} 
                                onChange={e => {
                                    setFilters({...filters, tipoRamo: e.target.value});
                                    setUiError(null);
                                }} 
                                className="flex-grow border border-gray-200 bg-gray-50 p-3 rounded-xl font-bold text-slate-700 outline-none focus:ring-2 focus:ring-slate-200 transition-all text-left"
                            >
                                <option value="">TODOS LOS RAMOS</option>
                                <option value="PATRIMONIALES">PATRIMONIALES</option>
                                <option value="ART">ART</option>
                            </select>
                            <button 
                                onClick={handleConsultar} 
                                disabled={!filters.entidadId || loading}
                                className="h-[50px] px-6 bg-slate-900 text-white rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-slate-800 disabled:opacity-30 shadow-lg transition-all active:scale-95 shrink-0 cursor-pointer flex items-center justify-center min-w-[90px]"
                            >
                                {loading ? (
                                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                ) : 'Generar'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {loading && (
                <div className="flex flex-col items-center justify-center py-20 bg-white rounded-3xl border border-gray-100 shadow-sm animate-pulse text-center no-print">
                    <div className="w-10 h-10 border-4 border-slate-100 border-t-slate-900 rounded-full animate-spin mb-4"></div>
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Procesando estados e importes de liquidación...</p>
                </div>
            )}

            {/* BARRA DE TOTALES GERENCIALES (DASHBOARD) */}
            {!loading && liquidaciones.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 no-print animate-fade-in">
                    <div className="bg-slate-50 border border-slate-200 p-5 rounded-2xl flex flex-col justify-center">
                        <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider">Total Comisiones Brutas</span>
                        <span className="text-2xl font-black text-slate-800 mt-1 font-mono">{formatCurrency(totalesComerciales.brutoTotal)}</span>
                    </div>
                    <div className="bg-rose-50/50 border border-rose-100 p-5 rounded-2xl flex flex-col justify-center">
                        <span className="text-[9px] font-black text-rose-500 uppercase tracking-wider">Descuentos e Incentivos</span>
                        <span className="text-2xl font-black text-rose-700 mt-1 font-mono">-{formatCurrency(totalesComerciales.retencionesTotal)}</span>
                    </div>
                    <div className="bg-indigo-50 border border-indigo-100 p-5 rounded-2xl flex flex-col justify-center">
                        <span className="text-[9px] font-black text-indigo-600 uppercase tracking-wider">Monto Neto a Liquidar</span>
                        <span className="text-2xl font-black text-indigo-900 mt-1 font-mono">{formatCurrency(totalesComerciales.netoTotal)}</span>
                    </div>
                </div>
            )}

            {/* TABLA DE RESULTADO EN PANTALLA */}
            {!loading && liquidaciones.length > 0 && (
                <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden animate-fade-in text-left">
                    <div className="flex justify-between items-center px-8 py-6 border-b border-gray-50 no-print select-none">
                        <div className="text-left">
                            <h3 className="font-black text-slate-800 uppercase tracking-tight text-lg">Resultado de Liquidación</h3>
                            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">
                                Rol: {filters.modoLiquidacion} | Entidad: <span className="text-slate-700 font-black">{entidadSeleccionadaNombre}</span>
                            </p>
                        </div>
                        <button 
                            onClick={handleDownloadPDF} 
                            disabled={loading}
                            className="flex items-center gap-2 px-6 py-2.5 bg-emerald-50 text-emerald-700 border border-emerald-100 rounded-xl font-black uppercase text-[10px] hover:bg-emerald-100 transition-all cursor-pointer active:scale-95 transform shadow-xs disabled:opacity-50"
                        >
                            {loading ? 'Compilando...' : '📥 Descargar PDF Oficial'}
                        </button>
                    </div>

                    {/* VISTA EN PANTALLA */}
                    <div className="overflow-x-auto max-h-[500px] custom-scrollbar no-print">
                        <table className="w-full text-xs text-left border-separate border-spacing-0">
                            <thead className="bg-slate-50 border-b border-gray-200 sticky top-0 z-10 font-black text-[9px] text-slate-400 uppercase tracking-wider">
                                <tr>
                                    <th className="py-4 px-6 border-b">Póliza/Contrato</th>
                                    <th className="py-4 px-2 border-b">Ramo</th>
                                    <th className="py-4 px-2 border-b">Cía</th>
                                    <th className="py-4 px-2 border-b">Cliente</th>
                                    <th className="py-4 px-2 border-b text-center">Base</th>
                                    <th className="py-4 px-2 border-b text-right">Importe $</th>
                                    <th className="py-4 px-2 border-b text-center">% Part.</th>
                                    <th className="py-4 px-2 border-b text-right">Com. Bruta</th>
                                    <th className="py-4 px-2 border-b text-center">Desc. %</th>
                                    <th className="py-4 px-6 border-b text-right font-black text-slate-800">Neta $</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 text-left font-semibold text-slate-600">
                                {liquidaciones.map((row, i) => (
                                    <tr key={i} className="hover:bg-slate-50/50 transition-colors">
                                        <td className="py-3.5 px-6 font-mono font-black text-gray-500 text-[11px]">{row.nroDocumento}</td>
                                        <td className="py-3.5 px-2">
                                            <span className={`text-[8px] font-black px-1.5 py-0.5 rounded border ${row.tipoRamoLabel === 'ART' ? 'bg-orange-50 text-orange-600 border-orange-100' : 'bg-blue-50 text-blue-600 border-blue-100'}`}>
                                                {row.tipoRamoLabel}
                                            </span>
                                        </td>
                                        <td className="py-3.5 px-2 font-bold text-gray-800 truncate max-w-[110px]">{row.compania}</td>
                                        <td className="py-3.5 px-2 truncate max-w-[150px] uppercase font-bold text-slate-700">{row.clienteNombre} {row.clienteApellido || ''}</td>
                                        <td className="py-3.5 px-2 text-center text-[8px] uppercase font-black text-slate-400 bg-slate-50/50">{row.baseLabel}</td>
                                        <td className="py-3.5 px-2 text-right font-mono font-bold text-slate-500">{formatCurrency(row.base)}</td>
                                        <td className="py-3.5 px-2 text-center font-black text-indigo-600 font-mono">{formatPercent(row.res_pct)}</td>
                                        <td className="py-3.5 px-2 text-right text-gray-600 font-mono font-bold">{formatCurrency(row.res_bruto)}</td>
                                        <td className="py-3.5 px-2 text-center text-red-500 font-black font-mono">{row.res_desc > 0 ? `-${row.res_desc}%` : '-'}</td>
                                        <td className="py-3.5 px-6 text-right font-black text-slate-900 bg-slate-50/30 font-mono text-sm">{formatCurrency(row.res_neto)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
            
            <style>{`
                .custom-scrollbar::-webkit-scrollbar { width: 6px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #e5e7eb; border-radius: 10px; }
            `}</style>
        </div>
    );
};

export default ReportesTab;